/**
 * Phone Smarty viewport gate (PS-0 / PS-7) + Rocket Launcher RL-5.
 *
 * LOCKED: Phone Smarty mounts only on real smartphones — never as the default
 * for laptop/desktop Chrome (Windows touchscreen + DPI-scaled height < 768 is
 * still a computer; an installed PWA `display-mode: standalone` window is too).
 *
 * Classifier (order matters):
 *  1. Smartphone UA (iPhone / Android Mobile / Mobi) → Phone Smarty always,
 *     including landscape and Home Screen tiles (RL-5).
 *  2. Mouse or trackpad (`pointer: fine` or `hover: hover`) → desktop always.
 *     Do not use maxTouchPoints alone — Windows laptops often report > 0.
 *  3. Standalone without desktop input (phone already caught in 1; leftover
 *     is tablet/kiosk) → Phone Smarty.
 *  4. Else short edge < 768 AND coarse/touch → Phone Smarty (iPad Mini, etc.).
 *
 * Chrome DevTools iPhone mode spoofs the UA, so step 1 still mounts the shell.
 */

import { isStandalone } from './homeScreenInstall'

export const PHONE_SMARTY_MAX_WIDTH = 768

const DESKTOP_INPUT_QUERIES = ['(hover: hover)', '(pointer: fine)']
const TOUCH_POINTER_QUERY = '(pointer: coarse)'
const VIEWPORT_WATCH_QUERIES = DESKTOP_INPUT_QUERIES.concat([
  TOUCH_POINTER_QUERY,
  '(hover: none)',
  '(display-mode: standalone)',
])

/**
 * True when the UA is a phone (not a tablet, not Windows/Mac laptop Chrome).
 * @param {string} [ua]
 */
export function isSmartphoneUserAgent (ua) {
  const s = String(ua || '')
  if (!s) return false
  if (/iPhone|iPod/i.test(s)) return true
  if (/Windows Phone|IEMobile|webOS|BlackBerry/i.test(s)) return true
  if (/Android/i.test(s) && /Mobile/i.test(s)) return true
  // Chrome DevTools / some WebViews; iPad must not match.
  if (/\bMobi\b/i.test(s) && !/iPad/i.test(s)) return true
  return false
}

function liveUserAgent () {
  if (typeof navigator === 'undefined') return ''
  return navigator.userAgent || ''
}

/**
 * Mouse or trackpad is the primary pointer — laptop/desktop, including
 * touchscreen 2-in-1s in laptop mode. Phones are `hover: none` + `pointer: coarse`.
 */
export function isDesktopLikeInput () {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  for (let i = 0; i < DESKTOP_INPUT_QUERIES.length; i++) {
    try {
      if (window.matchMedia(DESKTOP_INPUT_QUERIES[i]).matches) return true
    } catch (e) { /* ignore */ }
  }
  return false
}

/**
 * Coarse primary pointer (finger). Used only after desktop-input has been ruled out.
 */
export function isLikelyTouchDevice () {
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    try {
      if (window.matchMedia(TOUCH_POINTER_QUERY).matches) return true
    } catch (e) { /* ignore */ }
  }
  // Fallback when matchMedia pointer queries are unavailable (older engines).
  if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) {
    return true
  }
  return false
}

/**
 * Pure evaluator for tests and window reads.
 * @param {{
 *   width?: number,
 *   height?: number,
 *   touch?: boolean,
 *   standalone?: boolean,
 *   phone?: boolean,
 *   desktopInput?: boolean,
 *   ua?: string
 * }} [dims]
 */
export function evaluatePhoneSmartyViewport (dims) {
  const opts = dims || {}

  const phone = opts.phone != null
    ? !!opts.phone
    : isSmartphoneUserAgent(opts.ua != null ? opts.ua : liveUserAgent())
  if (phone) return true

  const desktopInput = opts.desktopInput != null
    ? !!opts.desktopInput
    : isDesktopLikeInput()
  if (desktopInput) return false

  const standalone = opts.standalone != null
    ? !!opts.standalone
    : (typeof window !== 'undefined' && isStandalone())
  if (standalone) return true

  const w = opts.width != null
    ? Number(opts.width)
    : (typeof window !== 'undefined' ? window.innerWidth : PHONE_SMARTY_MAX_WIDTH)
  const h = opts.height != null
    ? Number(opts.height)
    : (typeof window !== 'undefined' ? window.innerHeight : PHONE_SMARTY_MAX_WIDTH)
  const shortEdge = Math.min(w, h)
  const touch = opts.touch != null ? !!opts.touch : isLikelyTouchDevice()
  return shortEdge < PHONE_SMARTY_MAX_WIDTH && touch
}

/**
 * Whether the current (or passed) viewport should mount Phone Smarty.
 *
 * @param {number|{ width?: number, height?: number, touch?: boolean, standalone?: boolean, phone?: boolean, desktopInput?: boolean, ua?: string }} [arg]
 *   Omit for live window. Number = short-edge-only check (legacy test helper).
 */
export function isPhoneSmartyViewport (arg) {
  if (typeof arg === 'number') {
    return arg < PHONE_SMARTY_MAX_WIDTH
  }
  if (arg && typeof arg === 'object') {
    return evaluatePhoneSmartyViewport(arg)
  }
  return evaluatePhoneSmartyViewport()
}

function subscribeMedia (query, handler, bucket) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return
  }
  try {
    const mql = window.matchMedia(query)
    if (mql.addEventListener) mql.addEventListener('change', handler)
    else if (mql.addListener) mql.addListener(handler)
    bucket.push({ mql, handler })
  } catch (e) { /* ignore */ }
}

/**
 * Subscribe to viewport / orientation / pointer changes across the Phone Smarty gate.
 * Returns an unsubscribe function.
 */
export function watchPhoneSmartyViewport (callback) {
  if (typeof window === 'undefined') {
    callback(isPhoneSmartyViewport())
    return () => {}
  }
  const fire = () => callback(isPhoneSmartyViewport())
  fire()
  window.addEventListener('resize', fire, { passive: true })
  window.addEventListener('orientationchange', fire)
  const media = []
  for (let i = 0; i < VIEWPORT_WATCH_QUERIES.length; i++) {
    subscribeMedia(VIEWPORT_WATCH_QUERIES[i], fire, media)
  }
  return () => {
    window.removeEventListener('resize', fire)
    window.removeEventListener('orientationchange', fire)
    for (let i = 0; i < media.length; i++) {
      const { mql, handler } = media[i]
      if (mql.removeEventListener) mql.removeEventListener('change', handler)
      else if (mql.removeListener) mql.removeListener(handler)
    }
  }
}
